import { useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Dropdown, DropdownMenu, DropdownToggle } from "reactstrap";

import { withTranslation } from "react-i18next";

import user1 from "../../../assets/images/users/avatar-1.jpg";
import { performLogout } from "../../../helpers/performLogout";
import { selectRole, selectUser } from "../../../store/auth/selectors";

const getInitials = (fullName) => {
  if (!fullName || typeof fullName !== "string") return "U";
  const trimmed = fullName.trim();
  if (!trimmed) return "U";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const AVATAR_COLOR = "#00b3a0";

const getDisplayLabel = (user, role) => {
  if (!user) return role ?? "";
  if (import.meta.env.VITE_APP_DEFAULTAUTH === "firebase") {
    return user.email || role;
  }
  return user.username || user.email || role;
};

const getFullName = (user) => user?.user?.fullName || user?.fullName || "";

const ProfileMenu = (props) => {
  const [menu, setMenu] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = useSelector(selectRole);
  const user = useSelector(selectUser);

  const fullName = useMemo(() => getFullName(user), [user]);
  const username = useMemo(() => getDisplayLabel(user, role), [user, role]);
  const initials = useMemo(() => getInitials(fullName), [fullName]);

  const handleLogout = () => {
    setMenu(false);
    performLogout({ navigate, queryClient });
  };

  return (
    <Dropdown
      isOpen={menu}
      toggle={() => setMenu(!menu)}
      className="d-inline-block"
    >
      <DropdownToggle
        className="btn header-item "
        id="page-header-user-dropdown"
        tag="button"
      >
        {fullName ? (
          <div
            className="rounded-circle header-profile-user d-inline-flex align-items-center justify-content-center"
            style={{
              width: "36px",
              height: "36px",
              backgroundColor: AVATAR_COLOR,
              color: "#ffffff",
              fontSize: "14px",
              fontWeight: "600",
              lineHeight: "1",
              padding: "0",
            }}
          >
            {initials}
          </div>
        ) : (
          <img
            className="rounded-circle header-profile-user"
            src={user1}
            alt="Header Avatar"
          />
        )}
        <span className="d-none d-xl-inline-block ms-2 me-1">{username}</span>
        <i className="mdi mdi-chevron-down d-none d-xl-inline-block" />
      </DropdownToggle>
      <DropdownMenu className="dropdown-menu-end">
        {/* <DropdownItem tag="a" href="/profile">
          <i className="bx bx-user font-size-16 align-middle me-1" />
          {props.t("Profile")}
        </DropdownItem>
        <DropdownItem tag="a" href="/crypto-wallet">
          <i className="bx bx-wallet font-size-16 align-middle me-1" />
          {props.t("My Wallet")}
        </DropdownItem>
        <DropdownItem tag="a" href="auth-lock-screen">
          <i className="bx bx-lock-open font-size-16 align-middle me-1" />
          {props.t("Lock screen")}
        </DropdownItem> */}
        {role === "admin" && (
          <>
            <Link to="/settings" className="dropdown-item">
              <i className="bx bx-cog font-size-16 align-middle me-1" />
              <span>{props.t("Settings")}</span>
            </Link>
            <div className="dropdown-divider" />
          </>
        )}

        {role === "worker" && (
          <>
            <Link to="/user/me" className="dropdown-item">
              <i className="bx bx-user font-size-16 align-middle me-1" />
              <span>{props.t("workerMenuMyProfile", "My Profile")}</span>
            </Link>
            <Link to="/worker/settings" className="dropdown-item">
              <i className="bx bx-cog font-size-16 align-middle me-1" />
              <span>{props.t("workerMenuSettings", "Settings")}</span>
            </Link>
            <div className="dropdown-divider" />
          </>
        )}

        {role === "client" && (
          <>
            <Link to="/user/me" className="dropdown-item">
              <i className="bx bx-buildings font-size-16 align-middle me-1" />
              <span>
                {props.t("clientMenuCompanyProfile", "Company Profile")}
              </span>
            </Link>
            <Link to="/client/settings" className="dropdown-item">
              <i className="bx bx-cog font-size-16 align-middle me-1" />
              <span>{props.t("clientMenuSettings", "Settings")}</span>
            </Link>
            <div className="dropdown-divider" />
          </>
        )}

        <button type="button" className="dropdown-item" onClick={handleLogout}>
          <i className="bx bx-power-off font-size-16 align-middle me-1 text-danger" />
          <span>{props.t("Logout")}</span>
        </button>
      </DropdownMenu>
    </Dropdown>
  );
};

ProfileMenu.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(ProfileMenu);
